// Libs
import React, { useState, useEffect } from "react";
import { User } from "firebase/auth";

// Local
import EmailMarketingPage from "./EmailMarketingPage";
import { ExistingEmailTemplate } from "../../models/email-template";
import {
  ExistingEmailCampaign,
  EmailCampaignStatus,
} from "../../models/email-campaign";
import { EmailCampaignStatistics } from "../../database/email-marketing";
import { useAuthStore } from "../../store/firebase-auth";
import { DbRead } from "../../database";
import { useToastMessageStore } from "../../store/toast-messages";

interface Props {
  siteKey: string;
}

// Interface for campaign with statistics
export interface CampaignWithStats {
  campaign: ExistingEmailCampaign;
  statistics?: EmailCampaignStatistics;
}

export default function EmailMarketingContainer({ siteKey }: Props) {
  const firebaseUser = useAuthStore((state) => state.firebaseUser) as User;
  const addToastMessage = useToastMessageStore(
    (state) => state.addToastMessage,
  );

  const [templates, setTemplates] = useState<ExistingEmailTemplate[]>([]);
  const [campaigns, setCampaigns] = useState<ExistingEmailCampaign[]>([]);
  const [campaignStatistics, setCampaignStatistics] = useState<
    Map<string, EmailCampaignStatistics>
  >(new Map());
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [loadingCampaigns, setLoadingCampaigns] = useState(true);
  const [loadingStatistics, setLoadingStatistics] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch statistics for campaigns from the past 30 days
  const fetchCampaignStatistics = async (
    campaignsToFetch: ExistingEmailCampaign[],
  ) => {
    if (campaignsToFetch.length === 0) return;

    setLoadingStatistics(true);

    // Calculate 30 days ago
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    // Filter completed campaigns from past 30 days
    const recentCompletedCampaigns = campaignsToFetch.filter((campaign) => {
      if (
        !campaign.timestampCreated ||
        campaign.status !== EmailCampaignStatus.COMPLETED
      ) {
        return false;
      }
      const campaignDate = campaign.timestampCreated.toDate();
      return campaignDate >= thirtyDaysAgo;
    });

    try {
      // Fetch statistics for each campaign in parallel
      const statisticsPromises = recentCompletedCampaigns.map(
        async (campaign) => {
          try {
            const statsResponse = await DbRead.emailCampaigns.getStatistics({
              siteKey,
              campaignId: campaign.id,
            });

            // Handle nested statistics response structure (same as in EmailCampaignDetailsContainer)
            const statistics =
              (statsResponse as any).statistics || statsResponse;
            return { campaignId: campaign.id, statistics };
          } catch (error) {
            console.warn(
              `Failed to fetch statistics for campaign ${campaign.id}:`,
              error,
            );
            return { campaignId: campaign.id, statistics: null };
          }
        },
      );

      const results = await Promise.all(statisticsPromises);

      // Update the statistics map
      const newStatistics = new Map<string, EmailCampaignStatistics>();
      results.forEach(({ campaignId, statistics }) => {
        if (statistics) {
          newStatistics.set(campaignId, statistics);
        }
      });

      setCampaignStatistics(newStatistics);
    } catch (error) {
      console.error("Error fetching campaign statistics:", error);
      // Don't show error to user since statistics are for dashboard enhancement
    } finally {
      setLoadingStatistics(false);
    }
  };

  useEffect(() => {
    // Subscribe to templates
    const unsubscribeTemplates = DbRead.emailTemplates.subscribe({
      siteKey,
      onChange: (templatesData) => {
        setTemplates(templatesData);
        setLoadingTemplates(false);
      },
      onError: (error) => {
        console.error("Error loading templates:", error);
        addToastMessage({
          id: `templates-error-${Date.now()}`,
          message: "Failed to load email templates",
          type: "error",
          dialog: false,
        });
        setLoadingTemplates(false);
      },
    });

    // Subscribe to campaigns
    const unsubscribeCampaigns = DbRead.emailCampaigns.subscribe({
      siteKey,
      onChange: (campaignsData) => {
        setCampaigns(campaignsData);
        setLoadingCampaigns(false);

        // Fetch statistics for recent campaigns
        fetchCampaignStatistics(campaignsData);
      },
      onError: (error) => {
        console.error("Error loading campaigns:", error);
        addToastMessage({
          id: `campaigns-error-${Date.now()}`,
          message: "Failed to load email campaigns",
          type: "error",
          dialog: false,
        });
        setLoadingCampaigns(false);
      },
    });

    // Cleanup subscriptions on unmount
    return () => {
      unsubscribeTemplates();
      unsubscribeCampaigns();
    };
  }, [siteKey, addToastMessage]);

  return (
    <EmailMarketingPage
      siteKey={siteKey}
      templates={templates}
      campaigns={campaigns}
      campaignStatistics={campaignStatistics}
      loadingTemplates={loadingTemplates}
      loadingCampaigns={loadingCampaigns}
      loadingStatistics={loadingStatistics}
      error={error}
    />
  );
}
